import React, { useEffect, useState } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { Reveal } from "./shared/Reveal";
import FiveLayers from "./FiveLayers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "ev_admin_token";

/* Registry of every sendable flyer. `type:"image"` = a ready-made jpg;
   `type:"node"` = an on-page HTML block rendered to an image via html2canvas. */
const FLYERS = [
  { key: "energy-matters", type: "node", ref: "em-poster-node", file: "EnergyVaultGroup-EnergyMatters.png", title: "Energy Matters — Energy Vault Group" },
  { key: "flyer-back", type: "node", ref: "flyer-back-node", file: "EnergyVaultGroup-EnergySignature.png", title: "Every Building Has an Energy Signature — Energy Vault Group" },
  { key: "every-building", type: "image", ref: "/brand/mk_every_building.jpg", file: "EnergyVaultGroup-EveryBuilding.jpg", title: "Every Building Will Think For Itself — Energy Vault Group" },
  { key: "food-manufacturing", type: "image", ref: "/brand/mk_food_manufacturing.jpg", file: "EnergyVaultGroup-FoodManufacturing.jpg", title: "Food manufacturers are wasting up to 29% of their energy — Energy Vault Group" },
  { key: "stop-wasting", type: "image", ref: "/brand/mk_stop_wasting_energy.jpg", file: "EnergyVaultGroup-StopWastingEnergy.jpg", title: "Stop Wasting Energy, Start Protecting Profits — Energy Vault Group" },
  { key: "five-layers", type: "node", ref: "five-layers-node", file: "EnergyVaultGroup-FiveLayers.png", title: "Five Layers to Energy Independence — Energy Vault Group" },
  { key: "flyer-front", type: "node", ref: "flyer-front-node", file: "EnergyVaultGroup-Flyer-Front.png", title: "Where Is Your Waste Hiding? — Energy Vault Group" },
];

/* Turn one flyer into a shareable File */
async function flyerToFile(f) {
  if (f.type === "image") {
    const res = await fetch(f.ref);
    const blob = await res.blob();
    return new File([blob], f.file, { type: blob.type || "image/jpeg" });
  }
  const node = document.getElementById(f.ref);
  if (!node) return null;
  const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: "#0B1533", logging: false });
  const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
  return blob ? new File([blob], f.file, { type: "image/png" }) : null;
}

function downloadFile(file) {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url; a.download = file.name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/* Send one or many flyers via the native share sheet (WhatsApp, Email, Messages…).
   Falls back to saving the images if the device can't share files. */
async function sendFlyers(keys) {
  const chosen = FLYERS.filter((f) => keys.includes(f.key));
  if (!chosen.length) return;
  const files = [];
  for (const f of chosen) {
    try { const file = await flyerToFile(f); if (file) files.push(file); } catch (e) { /* skip */ }
  }
  if (!files.length) return;
  const title = files.length === 1 ? chosen[0].title : "Energy Vault Group — Marketing";
  if (navigator.canShare && navigator.canShare({ files })) {
    try { await navigator.share({ files, title, text: title }); return; }
    catch (e) { if (e && e.name === "AbortError") return; }
  }
  files.forEach(downloadFile);
}

const SendBtn = ({ onClick, testid, label = "Send · WhatsApp / Email" }) => (
  <button className="mk-send" onClick={onClick} data-testid={testid}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
    {label}
  </button>
);

const HIDING = [
  "Air compressors and compressed-air systems",
  "Compressed-air leaks",
  "CNC milling and turning machines",
  "Lathes and machining centres",
  "Welding and fabrication equipment",
  "Laser and waterjet cutting machinery",
  "Powder-coating systems and curing ovens",
  "Extraction and ventilation systems",
  "Heating, ventilation and air conditioning (HVAC)",
  "Refrigeration and cooling systems",
  "Chillers and process-cooling equipment",
  "Air-conditioning systems",
  "Pumps, motors, fans and drives",
  "Hydraulic and pneumatic equipment",
  "Boilers and industrial heating",
  "Electric ovens and heat-treatment equipment",
  "Lighting and lighting controls",
  "Workshop and warehouse heating",
  "IT equipment, servers and communications systems",
  "Standby and out-of-hours consumption",
  "Poor power factor",
  "Voltage imbalance and voltage quality",
  "Maximum-demand peaks",
  "Equipment operating simultaneously",
  "Ageing or inefficient machinery",
  "Poor maintenance and blocked filters",
  "Unnecessary weekend and overnight consumption",
];

const SOLUTIONS = [
  "Granular energy monitoring",
  "Circuit-level electricity monitoring",
  "Compressor monitoring and leak detection",
  "HVAC optimisation and smart controls",
  "Refrigeration and chiller optimisation",
  "Variable-speed drives for motors, pumps and fans",
  "LED lighting and intelligent controls",
  "Occupancy and daylight sensors",
  "Voltage optimisation",
  "Power-factor correction",
  "Peak-load management",
  "Automated equipment scheduling",
  "Solar PV",
  "Commercial battery storage",
  "Energy management systems",
  "Building management controls",
  "Water and gas monitoring",
  "Temperature, humidity and environmental sensors",
  "Equipment-performance alerts",
  "Predictive and preventative maintenance",
  "Centralised multi-site reporting",
  "AI-assisted energy analysis",
];

const REVEALS = [
  "Which machines and systems consume the most energy",
  "Where energy is being wasted",
  "What is running unnecessarily",
  "When damaging peaks occur",
  "Whether equipment is underperforming",
  "Which improvement should be prioritised",
  "The likely financial benefit before investment",
];

const STATEMENTS = [
  "We work with some of the biggest brand names in the commercial and manufacturing sectors.",
  "Coupled with our global technology partners, we bring together decades of commercial energy experience.",
  "Our state-of-the-art technology is already deployed across 70 countries.",
  "We identify exactly where, when and why energy is being wasted.",
  "We monitor individual circuits, machinery and critical assets — minute by minute.",
  "We expose energy losses hidden within compressors, HVAC, refrigeration, air conditioning, lighting, motors, pumps and production equipment.",
  "We identify unnecessary consumption, out-of-hours operation, equipment inefficiency and damaging peak demand.",
  "Once the waste has been identified, intelligent alerts and continuous monitoring help prevent it from returning.",
  "Every recommendation is supported by real operational evidence — not assumptions.",
  "We establish the facts before you invest in solar, batteries or replacement equipment.",
];

const BILL_GAPS = [
  "Which asset consumed the energy",
  "Where the waste occurred",
  "Why consumption increased",
  "Whether equipment is underperforming",
  "What was left running unnecessarily",
  "When costly peaks occurred",
  "What action should be taken",
];

const Tick = () => (
  <svg className="fl-tick" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const Cross = () => (
  <svg className="fl-cross" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const FlyerHeader = () => (
  <header className="flyer-top">
    <img className="flyer-logo" src="/brand/logo.png" alt="Energy Vault Group" />
    <span className="flyer-kbi-tag">Knowledge Before Investment</span>
  </header>
);

const FlyerFooter = () => (
  <footer className="flyer-foot">
    <div className="ff-name">Energy Vault Group</div>
    <div className="ff-line">Barnhill, Inchinnan Road, Houston PA4 9LU</div>
    <div className="ff-line">
      <span>0141 345 1256</span>
      <span className="ff-sep">·</span>
      <span>michael@energyvaultgroup.co.uk</span>
      <span className="ff-sep">·</span>
      <span>www.energyvaultgroup.co.uk</span>
    </div>
  </footer>
);

const FLYER_BG =
  "linear-gradient(180deg,rgba(11,21,51,.86),rgba(11,21,51,.92)),url('/brand/city_earth.jpg')";

export default function Flyer() {
  const [admin, setAdmin] = useState(false);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!t) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then(() => setAdmin(true))
      .catch(() => setAdmin(false));
  }, []);

  const toggle = (key) =>
    setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  const send = async (keys) => {
    if (!keys.length || busy) return;
    setBusy(true);
    try { await sendFlyers(keys); } finally { setBusy(false); }
  };

  // Per-flyer controls (checkbox + individual send), admin only.
  const actions = (key) => (
    <div className="mk-actions no-print">
      <label className={`mk-check ${selected.includes(key) ? "on" : ""}`}>
        <input type="checkbox" checked={selected.includes(key)} onChange={() => toggle(key)} data-testid={`select-${key}`} />
        <span>Select</span>
      </label>
      <SendBtn testid={`send-${key}`} label="Send this" onClick={() => send([key])} />
    </div>
  );

  return (
    <div className="flyerpage" style={{ backgroundImage: FLYER_BG }}>
      <div className="flyer-actions no-print">
        <a href="/" className="fl-back" data-testid="flyer-back">← Back to site</a>
        <div className="fl-actions-r">
          {admin ? (
            <span className="fl-adminbadge" data-testid="flyer-admin-badge">Admin mode · only you can send these</span>
          ) : (
            <a href="/gap-analysis" className="fl-print" data-testid="flyer-book-cta">Book Your Free Gap Analysis</a>
          )}
        </div>
      </div>

      {admin && (
        <div className="mk-adminbar no-print" data-testid="mk-adminbar">
          <div className="mk-adminbar-in">
            <span className="mk-ab-label" data-testid="mk-ab-count">
              {busy ? "Preparing…" : selected.length ? `${selected.length} selected` : "Tick flyers to send together, or send one at a time"}
            </span>
            <div className="mk-ab-btns">
              <button className="mk-ab-btn" disabled={!selected.length || busy} onClick={() => send(selected)} data-testid="send-selected">
                Send Selected{selected.length ? ` (${selected.length})` : ""}
              </button>
              <button className="mk-ab-btn primary" disabled={busy} onClick={() => send(FLYERS.map((f) => f.key))} data-testid="send-all">
                Send All
              </button>
              {selected.length > 0 && (
                <button className="mk-ab-clear" onClick={() => setSelected([])} data-testid="clear-selection">Clear</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== ENERGY MATTERS branded cover ===== */}
      <div id="em-poster-node" className="em-poster" data-testid="energy-matters"
        style={{ backgroundImage: "linear-gradient(180deg,rgba(11,21,51,.62),rgba(11,21,51,.90)),url('/brand/energy_matters_bg.jpg')" }}>
        <div className="em-top">
          <img className="em-logo" src="/brand/logo.png" alt="Energy Vault Group" />
        </div>
        <div className="em-center">
          <div className="em-eyebrow">Energy Vault Group</div>
          <h1 className="em-title">Energy<br /><span className="g">Matters.</span></h1>
          <div className="em-tag">Measure with confidence. Act with impact.</div>
        </div>
        <div className="em-foot">www.energyvaultgroup.co.uk</div>
      </div>
      {admin && actions("energy-matters")}

      {/* ===== ENERGY SIGNATURE / WHAT WE DO (moved above the others) ===== */}
      <div id="flyer-back-node" className="flyer flyer-side2" data-testid="flyer-side2">
        <FlyerHeader />

        <div className="flyer-body">
          <Reveal className="flyer-hero" tag="div">
            <p className="fh-experience">
              We have been in the energy industry for <b>30 years</b> — installing every form of renewable energy.
            </p>
            <div className="fh-kicker">EVERY BUILDING HAS AN</div>
            <h1 className="fh-title">Energy <span className="g">Signature.</span></h1>
            <p className="fh-sub">Your Energy Signature <span className="g">is Your Bill.</span></p>
            <p className="fh-intro">
              We unlock that bill — moving beyond the total cost to reveal, at a granular level, the individual
              assets, circuits and operating behaviours contributing to your energy waste.
            </p>
          </Reveal>

          <section className="flyer-sec">
            <h2 className="flyer-h2">What We Do</h2>
            <ul className="flyer-list statements">
              {STATEMENTS.map((item) => (
                <li key={item}><Tick />{item}</li>
              ))}
            </ul>
            <div className="fl-stat" data-testid="flyer-stat">
              <span className="fl-stat-num">87%</span>
              <span className="fl-stat-txt">energy losses found on <b>one monitored circuit alone</b>.</span>
            </div>
          </section>

          <section className="flyer-kbi">
            <h2 className="flyer-h2">Why Only Look Back at Energy Bills?</h2>
            <p className="fh-intro">
              A traditional energy bill tells you what you have <b>already spent</b>. It does not tell you:
            </p>
            <ul className="flyer-list reveal-list gaps">
              {BILL_GAPS.map((item) => (
                <li key={item}><Cross />{item}</li>
              ))}
            </ul>
          </section>

          <section className="flyer-cta">
            <div className="fc-mtv">We Don&rsquo;t Just Read the Bill.</div>
            <div className="fc-reveal">We Reveal the <span className="g">Story Behind It.</span></div>
            <div className="fc-tags">
              <span>Knowledge Before Investment</span>
              <span className="fc-dot">·</span>
              <span>Making the Invisible Visible</span>
            </div>
            <a href="/gap-analysis" className="btn no-print" data-testid="flyer-cta-2">Book Your Free Gap Analysis</a>
          </section>
        </div>

        <FlyerFooter />
      </div>
      {admin && actions("flyer-back")}

      {/* ===== Marketing flyers ===== */}
      <div className="mk-item" data-testid="mk-every-building">
        <img className="mk-img" src="/brand/mk_every_building.jpg" alt="Every Building Will Think for Itself — Energy Vault Group" loading="lazy" />
        {admin && actions("every-building")}
      </div>

      <div className="mk-item" data-testid="mk-food-manufacturing">
        <img className="mk-img" src="/brand/mk_food_manufacturing.jpg" alt="Food manufacturers are wasting up to 29% of their energy — Energy Vault Group" loading="lazy" />
        {admin && actions("food-manufacturing")}
      </div>

      <div className="mk-item" data-testid="mk-stop-wasting">
        <img className="mk-img" src="/brand/mk_stop_wasting_energy.jpg" alt="Stop Wasting Energy, Start Protecting Profits — Energy Vault Group" loading="lazy" />
        {admin && actions("stop-wasting")}
      </div>

      {/* ===== Five Layers to Energy Independence ===== */}
      <div id="five-layers-node" className="mk-fl5wrap">
        <FiveLayers />
      </div>
      {admin && actions("five-layers")}

      <div id="flyer-front-node" className="flyer" data-testid="flyer">
        <FlyerHeader />

        <div className="flyer-body">
          <Reveal className="flyer-hero" tag="div">
            <div className="fh-kicker">HOW MUCH ENERGY IS YOUR BUSINESS LOSING?</div>
            <h1 className="fh-title">Up to <span className="g">30%</span> of Your Electricity Bills Are Waste</h1>
            <p className="fh-intro">
              All commercial, manufacturing, engineering and hospitality facilities contain multiple energy-intensive
              systems. Even when individual machines appear to operate correctly, inefficient settings, unnecessary
              running hours and unmanaged peak demand can significantly increase electricity and gas bills.
            </p>
            <div className="fl-money" data-testid="flyer-money">
              <div className="fm-eg">Put That Into Money</div>
              <div className="fm-line">
                On a <span className="w">£100,000</span> annual energy bill, that&rsquo;s up to
                <span className="g"> £30,000</span> wasted — <span className="w">every single year.</span>
              </div>
            </div>
          </Reveal>

          <section className="flyer-sec">
            <h2 className="flyer-h2">Where Is Your Waste Hiding?</h2>
            <ul className="flyer-list cols">
              {HIDING.map((item) => (
                <li key={item}><Tick />{item}</li>
              ))}
            </ul>
          </section>

          <section className="flyer-sec">
            <h2 className="flyer-h2">Products &amp; Solutions That Could Make a Difference</h2>
            <ul className="flyer-list cols teal">
              {SOLUTIONS.map((item) => (
                <li key={item}><Tick />{item}</li>
              ))}
            </ul>
          </section>

          <section className="flyer-kbi">
            <h2 className="flyer-h2">Knowledge Before Investment</h2>
            <p className="fh-intro">
              Before recommending solar, batteries or new equipment, we establish exactly where, when and why energy
              is being consumed. Our <b>Gap Analysis</b> can reveal:
            </p>
            <ul className="flyer-list reveal-list">
              {REVEALS.map((item) => (
                <li key={item}><Tick />{item}</li>
              ))}
            </ul>
          </section>

          <section className="flyer-cta">
            <div className="fc-mtv">Making the Invisible Visible</div>
            <div className="fc-evg">Energy Vault Group</div>
            <div className="fc-nocost">No-Cost Initial Energy Gap Analysis</div>
            <a href="/gap-analysis" className="btn no-print" data-testid="flyer-cta">Book Your Free Gap Analysis</a>
          </section>
        </div>

        <footer className="flyer-foot">
          <div className="ff-name">Energy Vault Group</div>
          <div className="ff-line">Barnhill, Inchinnan Road, Houston PA4 9LU</div>
          <div className="ff-line">
            <span>0141 345 1256</span>
            <span className="ff-sep">·</span>
            <span>michael@energyvaultgroup.co.uk</span>
            <span className="ff-sep">·</span>
            <span>www.energyvaultgroup.co.uk</span>
          </div>
        </footer>
      </div>
      {admin && actions("flyer-front")}
    </div>
  );
}
